/**
 * @module lib/oauthFailure
 *
 * Turning an OAuth consent failure into the one thing the user has to be told.
 *
 * Every refusal reaches the consent screen the same way — a rejected promise —
 * but each one has a different way out: sign in again, use another account, ask
 * whoever configured the client to fix it, wait and retry. Collapsing them into
 * a single "errore" would leave the user with an authorization request they can
 * neither complete nor understand.
 *
 * It lives here rather than inside the component so the mapping can be tested
 * against errors the API layer really produces, instead of being asserted
 * against the component's source text.
 */

import { OAuthApiError } from '../stores/api.js';

/**
 * @typedef {Object} OAuthFailure
 * @property {string} kind Identifier of the i18n copy and of the way out offered
 * @property {string} message Raw detail from the server, shown as small print
 * @property {string[]} unsupportedScopes Scopes this server grants to nobody
 */

/**
 * Classify a consent failure.
 *
 * `kind` doubles as the i18n key stem (`oauth.errors.<kind>Title`/`Body`), so a
 * new kind needs copy in every locale before it can be returned from here.
 *
 * @param {unknown} error Anything a rejected `oauthApi` call threw
 * @returns {OAuthFailure}
 */
export function classifyOAuthFailure(error) {
  const apiError = error instanceof OAuthApiError ? error : null;
  const status = apiError?.status ?? 0;
  const code = apiError?.code ?? 'request_failed';
  const message = apiError?.message ?? (error instanceof Error ? error.message : String(error));
  const unsupported = Array.isArray(apiError?.payload?.unsupportedScopes)
    ? apiError.payload.unsupportedScopes
    : [];

  let kind = 'generic';
  if (status === 401) kind = 'sessionExpired';
  else if (code === 'subject_mismatch') kind = 'subjectMismatch';
  else if (code === 'access_denied') kind = 'accessDenied';
  else if (code === 'invalid_scope') kind = 'invalidScope';
  else if (code === 'invalid_target') kind = 'invalidTarget';
  else if (code === 'invalid_request') kind = 'missingChallenge';
  else if (code === 'oauth_request_invalid') kind = 'expiredChallenge';
  // A 503, a request that never left the browser, and a 200 carrying somebody
  // else's HTML are the same story: what answered was not the authorization
  // service, and retrying is the way out.
  else if (code === 'hydra_unavailable' || code === 'invalid_response' || status === 503 || status === 0) {
    kind = 'unavailable';
  }

  return { kind, message, unsupportedScopes: unsupported };
}

/**
 * The failure raised before any request is made, when the URL carries no
 * challenge at all.
 *
 * @returns {OAuthFailure}
 */
export function missingChallengeFailure() {
  return { kind: 'missingChallenge', message: '', unsupportedScopes: [] };
}

/**
 * The failure raised when a decision was recorded but came back with nowhere to
 * send the browser — there is no handshake left to continue, only a retry.
 *
 * @returns {OAuthFailure}
 */
export function missingRedirectFailure() {
  return { kind: 'generic', message: '', unsupportedScopes: [] };
}
